import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, CheckCircle, Plus, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { leaveApi, balanceApi } from '../../services/api';

interface LeaveApplication {
  applicationId: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  status: string;
}

interface LeaveBalance {
  leaveType: string;
  total: number;
  taken: number;
  pending: number;
  available: number;
  approved?: number;
  accumulatedHours?: number;
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [applications, setApplications] = useState<LeaveApplication[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.userId) return;

      setIsLoading(true);
      setError('');

      try {
        // Fetch applications and balances in parallel
        const [appsResponse, balancesResponse] = await Promise.all([
          leaveApi.listApplications().catch(() => ({ applications: [] })),
          balanceApi.getBalance(user.userId).catch(() => ({ balances: [] })),
        ]);

        setApplications((appsResponse as { applications: LeaveApplication[] }).applications || []);
        setBalances((balancesResponse as { balances: LeaveBalance[] }).balances || []);
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user?.userId]);

  // Filter applications
  const pendingApplications = applications.filter(
    app => ['PENDING_RECOMMENDATION', 'PENDING_APPROVAL'].includes(app.status)
  );

  const upcomingApproved = applications.filter(
    app => app.status === 'APPROVED' && new Date(app.startDate) >= new Date()
  );

  // Get balances by type
  const getBalance = (type: string): LeaveBalance => {
    return (
      balances.find(b => b.leaveType === type) || {
        leaveType: type,
        total: 0,
        taken: 0,
        pending: 0,
        available: 0,
      }
    );
  };

  const annualBalance = getBalance('ANNUAL');
  const sickBalance = getBalance('SICK');
  const familyBalance = getBalance('FAMILY');

  const formatLeaveType = (type: string) => {
    return type.charAt(0) + type.slice(1).toLowerCase();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-600">
            Welcome back{user?.firstName ? `, ${user.firstName}` : ''}! Here's your leave summary.
          </p>
        </div>
        <Link to="/leave/apply" className="btn-primary flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Apply for Leave
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Balance Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Annual Leave</p>
              <p className="mt-1 text-3xl font-bold text-gray-900">{annualBalance.available}</p>
              <p className="text-sm text-gray-500">of {annualBalance.total} days available</p>
            </div>
            <div className="rounded-full bg-green-100 p-3">
              <Calendar className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-500">
            {annualBalance.taken} taken | {annualBalance.pending} pending
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Sick Leave</p>
              <p className="mt-1 text-3xl font-bold text-gray-900">{sickBalance.available}</p>
              <p className="text-sm text-gray-500">of {sickBalance.total} days (3-year cycle)</p>
            </div>
            <div className="rounded-full bg-red-100 p-3">
              <Clock className="h-6 w-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-500">
            {sickBalance.taken} taken
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Family Leave</p>
              <p className="mt-1 text-3xl font-bold text-gray-900">{familyBalance.available}</p>
              <p className="text-sm text-gray-500">of {familyBalance.total} days available</p>
            </div>
            <div className="rounded-full bg-blue-100 p-3">
              <CheckCircle className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-500">
            {familyBalance.taken} taken this year
          </div>
        </div>
      </div>

      {/* Pending Applications */}
      <div className="card">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Pending Applications</h2>
        </div>
        <div className="p-6">
          {pendingApplications.length === 0 ? (
            <p className="text-center text-gray-500">No pending applications</p>
          ) : (
            <div className="space-y-4">
              {pendingApplications.map(app => (
                <Link
                  key={app.applicationId}
                  to={`/leave/${app.applicationId}`}
                  className="flex items-center justify-between rounded-lg border border-gray-200 p-4 transition-colors hover:bg-gray-50"
                >
                  <div>
                    <p className="font-medium text-gray-900">{formatLeaveType(app.leaveType)} Leave</p>
                    <p className="text-sm text-gray-500">
                      {app.startDate} - {app.endDate} ({app.totalDays} {app.totalDays === 1 ? 'day' : 'days'})
                    </p>
                  </div>
                  <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800">
                    {app.status === 'PENDING_RECOMMENDATION' ? 'Pending Recommendation' : 'Pending Approval'}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Leave */}
      <div className="card">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming Approved Leave</h2>
        </div>
        <div className="p-6">
          {upcomingApproved.length === 0 ? (
            <p className="text-center text-gray-500">No upcoming leave</p>
          ) : (
            <div className="space-y-4">
              {upcomingApproved.map(leave => (
                <Link
                  key={leave.applicationId}
                  to={`/leave/${leave.applicationId}`}
                  className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4 transition-colors hover:bg-green-100"
                >
                  <div>
                    <p className="font-medium text-gray-900">{formatLeaveType(leave.leaveType)} Leave</p>
                    <p className="text-sm text-gray-600">
                      {leave.startDate} - {leave.endDate} ({leave.totalDays} {leave.totalDays === 1 ? 'day' : 'days'})
                    </p>
                  </div>
                  <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
                    Approved
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
